/**
 * Transformers for report API responses
 */

import { SHIFT_KEYS } from '@/constants/shifts';

/**
 * Transform meals for restaurant companies from API format to table-ready format
 * @param {Object} apiData - API response object with companies as keys
 * @returns {Array} Array of companies with meals ready for v-data-table
 */
export const transformMealsForRestaurantCompanies = (apiData) => {
    if (!apiData || typeof apiData !== 'object') {
        return [];
    }

    const companies = [];
    let companyId = 1;

    Object.keys(apiData).forEach((companyName) => {
        const companyData = apiData[companyName];
        const mealsMap = new Map();

        // Parse all shifts and group by meal
        Object.keys(companyData).forEach((shiftCode) => {
            const shiftData = companyData[shiftCode];

            if (shiftData.customer && Array.isArray(shiftData.customer)) {
                shiftData.customer.forEach((meal) => {
                    if (!mealsMap.has(meal.id)) {
                        mealsMap.set(meal.id, {
                            id: meal.id,
                            name: meal.name,
                            shift_one: { orders: 0, taken: 0 },
                            shift_two: { orders: 0, taken: 0 },
                            shift_three: { orders: 0, taken: 0 },
                            shift_four: { orders: 0, taken: 0 }
                        });
                    }

                    // Extract shift number from code (e.g., "1-smena" -> 1)
                    const shiftId = parseInt(shiftCode.split('-')[0]);
                    const shiftKey = SHIFT_KEYS[shiftId];

                    if (shiftKey) {
                        mealsMap.get(meal.id)[shiftKey] = {
                            orders: meal.qunatity || 0,
                            taken: meal.quantity_payed || 0
                        };
                    }
                });
            }
        });

        // Convert meals map to array and add row numbers + totals
        const meals = Array.from(mealsMap.values()).map((meal, index) => {
            const total = Object.values(SHIFT_KEYS).reduce((sum, key) => sum + meal[key].orders, 0);
            const totalPayed = Object.values(SHIFT_KEYS).reduce((sum, key) => sum + meal[key].taken, 0);

            return {
                num: index + 1,
                ...meal,
                total,
                totalPayed
            };
        });

        // Calculate totals for all shifts
        const totals = {
            shift_one: 0,
            shift_one_payed: 0,
            shift_two: 0,
            shift_two_payed: 0,
            shift_three: 0,
            shift_three_payed: 0,
            shift_four: 0,
            shift_four_payed: 0,
            total: 0,
            totalPayed: 0
        };

        meals.forEach(meal => {
            Object.keys(SHIFT_KEYS).forEach(shiftId => {
                const key = SHIFT_KEYS[shiftId];
                totals[key] += meal[key].orders;
                totals[key + '_payed'] += meal[key].taken;
            });
            totals.total += meal.total;
            totals.totalPayed += meal.totalPayed;
        });

        companies.push({
            id: companyId++,
            name: companyName,
            meals,
            totals
        });
    });

    return companies;
};

/**
 * Transform meals for packages tab (includes customer and restaurant quantities)
 * @param {Object} apiData - API response object with companies as keys
 * @returns {Array} Array of companies with meals showing customer/restaurant breakdown
 */
export const transformMealsForPackages = (apiData) => {
    if (!apiData || typeof apiData !== 'object') {
        return [];
    }

    const companies = [];
    let companyId = 1;

    Object.keys(apiData).forEach((companyName) => {
        const companyData = apiData[companyName];
        const mealsMap = new Map();

        // Parse all shifts and group by meal
        Object.keys(companyData).forEach((shiftCode) => {
            const shiftData = companyData[shiftCode];

            // Process both customer and restaurant orders
            ['customer', 'restaurant'].forEach((origin) => {
                if (shiftData[origin] && Array.isArray(shiftData[origin])) {
                    shiftData[origin].forEach((meal) => {
                        if (!mealsMap.has(meal.id)) {
                            mealsMap.set(meal.id, {
                                id: meal.id,
                                name: meal.name,
                                shift_one: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
                                shift_two: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
                                shift_three: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
                                shift_four: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 }
                            });
                        }

                        const shiftId = parseInt(shiftCode.split('-')[0]);
                        const shiftKey = SHIFT_KEYS[shiftId];

                        if (shiftKey) {
                            const mealData = mealsMap.get(meal.id);
                            if (origin === 'customer') {
                                mealData[shiftKey].customerQuantity += meal.qunatity || 0;
                            } else if (origin === 'restaurant') {
                                mealData[shiftKey].restaurantQuantity += meal.qunatity || 0;
                            }
                            mealData[shiftKey].totalQuantity =
                                mealData[shiftKey].customerQuantity + mealData[shiftKey].restaurantQuantity;
                        }
                    });
                }
            });
        });

        // Convert meals map to array and add row numbers + totals
        const meals = Array.from(mealsMap.values()).map((meal, index) => {
            const total = Object.values(SHIFT_KEYS).reduce((sum, key) => sum + meal[key].totalQuantity, 0);

            return {
                num: index + 1,
                ...meal,
                total
            };
        });

        // Calculate totals for all shifts
        const totals = {
            shift_one: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
            shift_two: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
            shift_three: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
            shift_four: { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 },
            total: 0
        };

        meals.forEach(meal => {
            Object.keys(SHIFT_KEYS).forEach(shiftId => {
                const key = SHIFT_KEYS[shiftId];
                totals[key].customerQuantity += meal[key].customerQuantity;
                totals[key].restaurantQuantity += meal[key].restaurantQuantity;
                totals[key].totalQuantity += meal[key].totalQuantity;
            });
            totals.total += meal.total;
        });

        companies.push({
            id: companyId++,
            name: companyName,
            meals,
            totals
        });
    });

    return companies;
};

/**
 * Transform company meals with totals across shifts
 * @param {Object} apiData - API response object
 * @returns {Array} Array of companies with aggregated meal data
 */
export const transformMealsWithTotals = (apiData) => {
    if (!apiData || typeof apiData !== 'object') {
        return [];
    }

    const companies = [];
    let companyId = 1;

    Object.keys(apiData).forEach((companyName) => {
        const companyData = apiData[companyName];
        const mealsMap = new Map();

        // Aggregate meals across all shifts
        Object.keys(companyData).forEach((shiftCode) => {
            const shiftData = companyData[shiftCode];

            if (shiftData.customer && Array.isArray(shiftData.customer)) {
                shiftData.customer.forEach((meal) => {
                    if (!mealsMap.has(meal.id)) {
                        mealsMap.set(meal.id, {
                            id: meal.id,
                            name: meal.name,
                            totalOrders: 0,
                            totalTaken: 0,
                            shifts: []
                        });
                    }

                    const mealData = mealsMap.get(meal.id);
                    mealData.totalOrders += meal.qunatity || 0;
                    mealData.totalTaken += meal.quantity_payed || 0;
                    mealData.shifts.push({
                        code: shiftCode,
                        orders: meal.qunatity || 0,
                        taken: meal.quantity_payed || 0
                    });
                });
            }
        });

        companies.push({
            id: companyId++,
            name: companyName,
            meals: Array.from(mealsMap.values())
        });
    });

    return companies;
};
